/**
 * GT-STATUS-SOURCE-OF-TRUTH-LIVEMAP-FALLBACK-AND-RESIDUALS-03 — the ONE
 * canonical Connection classification, shared by the API and the web app.
 *
 * Phase-0 of this sprint found the Fleet summary popup and the Live Map
 * Data table disagreed on Online/Offline because they used DIFFERENT
 * formulas: the popup counted `operational_status` (movement-based, a
 * column that is never downgraded after silence) while every per-row
 * badge classified CONNECTION from device packet freshness
 * (`lastPacketAt`). This module is the single source of truth for the
 * Connection axis so the count and the rows can never diverge again.
 *
 * Connection is orthogonal to Motion (speed), Location (coordinates) and
 * Activity/Operational (business lifecycle) — those are classified
 * elsewhere; this file owns ONLY Connection.
 *
 * A device is `online` up to `CONNECTION_ONLINE_MS`, `stale` up to
 * `CONNECTION_STALE_MS`, `offline` beyond (Owner values). Both
 * `services/api` (fleet summary counts) and `apps/web`
 * (`connectionStatus.ts` per-row badges) import these so there is exactly
 * one definition.
 *
 * This module owns every threshold on those axes. Nothing downstream may
 * carry its own copy of a window, an age limit or a speed floor — the
 * `status-thresholds.source-guard.test.ts` suite fails CI when a literal
 * reappears at a call site, because every drift bug this file has had to
 * absorb started as a well-meant local constant.
 */

/** A device seen within this window is `online`. */
export const CONNECTION_ONLINE_MS = 35 * 60_000;
/** Older than online but within this window is `stale`; beyond is `offline`. */
export const CONNECTION_STALE_MS = 60 * 60_000;

export type ConnectionState = "online" | "stale" | "offline" | "never_seen";

/**
 * GT-LIVEMAP-TRIPS-BUGFIXES — the live-position look-back window, DERIVED
 * from the Connection threshold instead of being set by hand.
 *
 * The bug this closes: the Live Map Data table sourced speed, supply
 * voltage and satellites from a positions query hard-coded to
 * `sinceSeconds: 600` (10 min) while the Connection badge on the SAME row
 * classified `online` at 15 min. Every vehicle whose newest packet fell in
 * the 5-minute gap rendered a green "Online" badge next to three empty
 * cells, because no position row came back for it at all. The same literal
 * `600` was copied into three call sites (Live Map, the global status bar
 * and the Dashboard), so the drift was reproduced three times over.
 *
 * Deriving the window makes that class of bug structurally impossible: the
 * window is BY CONSTRUCTION never shorter than the online threshold, so
 * every vehicle the UI calls `online` is guaranteed to be inside the
 * queried window. Raising `CONNECTION_ONLINE_MS` moves the window with it;
 * the two can no longer be changed independently.
 *
 * The guard margin absorbs clock skew between the browser's `now` and the
 * database's `now`, plus request latency, so a vehicle sitting exactly on
 * the online edge does not lose its row to a race.
 */
export const LIVE_POSITION_WINDOW_GUARD_MS = 2 * 60_000;

/**
 * Hard ceiling the `/v1/telemetry/positions/latest` query schema enforces.
 * Exported here so the API schema and this derivation share ONE number
 * rather than each carrying its own literal.
 */
export const LIVE_POSITION_WINDOW_MAX_SECONDS = 3600;

/**
 * The look-back every live-position consumer MUST pass. Never hand-set a
 * `sinceSeconds` literal at a call site — a source guard test fails CI if
 * one reappears.
 */
export const LIVE_POSITION_WINDOW_MS = CONNECTION_ONLINE_MS + LIVE_POSITION_WINDOW_GUARD_MS;

/** The same window in whole seconds, ready for the query parameter. */
export const LIVE_POSITION_WINDOW_SECONDS = Math.ceil(LIVE_POSITION_WINDOW_MS / 1000);

// Fail loudly at module load rather than letting the server silently clamp
// the window back below the online threshold — a silent clamp would
// re-open exactly the dead band this module exists to prevent.
if (LIVE_POSITION_WINDOW_SECONDS > LIVE_POSITION_WINDOW_MAX_SECONDS) {
  throw new Error(
    `CONNECTION_ONLINE_MS (${CONNECTION_ONLINE_MS} ms) + guard exceeds the ` +
      `${LIVE_POSITION_WINDOW_MAX_SECONDS}s cap of /v1/telemetry/positions/latest ` +
      `(needs ${LIVE_POSITION_WINDOW_SECONDS}s). Raise the endpoint cap in the same ` +
      `change that raises the threshold — do not clamp the window.`,
  );
}

/**
 * Classify Connection from the newest packet timestamp (ms epoch) and a
 * reference `now` (ms). `null`/non-finite ⇒ `never_seen`. Pure +
 * deterministic so the API and web compute byte-identical buckets.
 */
export function classifyConnectionState(
  lastPacketAtMs: number | null | undefined,
  nowMs: number,
): ConnectionState {
  if (lastPacketAtMs == null || !Number.isFinite(lastPacketAtMs)) return "never_seen";
  const age = nowMs - lastPacketAtMs;
  if (age <= CONNECTION_ONLINE_MS) return "online";
  if (age <= CONNECTION_STALE_MS) return "stale";
  return "offline";
}

/**
 * GT-GI-LIVE-STATE-SOURCE-OF-TRUTH-AND-FLEET-PULSE-POPOVER-RECOVERY-01 — the ONE
 * canonical Motion classification, promoted here from
 * `apps/web/src/vehicles/connectionStatus.ts` so the per-row badges, the Fleet
 * Pulse header counters (`deriveOperationalCounters`), AND the server-side Genius
 * Intelligence fleet-status tool all classify "currently driving / stopped" with
 * the IDENTICAL rule. The header previously drifted: it counted a stale
 * `operational_status` column as live Driving, presenting last-known state of an
 * OFFLINE vehicle as current movement.
 *
 * Motion is meaningful ONLY while the device is currently online. A stale/offline
 * device is `unknown` — it must NEVER show a live "Driving"/"Stopped". A speed at
 * or below `DRIVING_MIN_KPH` is `stopped`, never `driving` (Owner rule). A speed
 * sample older than `MOTION_SAMPLE_MAX_AGE_MS` is `unknown` even on an online
 * device.
 */
export type MotionState = "driving" | "stopped" | "unknown";

/** Speed strictly above this (km/h) is "driving"; at/below is "stopped". */
export const DRIVING_MIN_KPH = 3;
/**
 * A speed sample older than this is not trusted for live motion.
 *
 * DERIVED from the Connection threshold rather than carried as its own
 * literal. The two were coincidentally equal (both 15 min) and would have
 * silently diverged the moment one was tuned. Tying them expresses the
 * actual rule: a sample is trusted for exactly as long as the device still
 * counts as online — no longer, and never shorter (a shorter value would
 * make an online device's motion `unknown`, which is the same class of
 * dead band as the position-window bug above).
 */
export const MOTION_SAMPLE_MAX_AGE_MS = CONNECTION_ONLINE_MS;

export function classifyMotionState(
  speedKph: number | null | undefined,
  sampleAtMs: number | null | undefined,
  connection: ConnectionState,
  nowMs: number,
): MotionState {
  // Motion is only meaningful while the device is actually communicating.
  if (connection !== "online") return "unknown";
  if (speedKph == null || !Number.isFinite(speedKph)) return "unknown";
  if (
    sampleAtMs != null &&
    Number.isFinite(sampleAtMs) &&
    nowMs - sampleAtMs > MOTION_SAMPLE_MAX_AGE_MS
  ) {
    return "unknown";
  }
  return speedKph > DRIVING_MIN_KPH ? "driving" : "stopped";
}

/**
 * GT-LIVEMAP-TRIPS-BUGFIXES (B2) — the ONE canonical decision for how a
 * speed cell renders.
 *
 * Before this, the Live Map Data table piped `pos?.speedKph` straight into
 * the quantity formatter, so the same fleet showed `0 km/h` on one row and
 * `—` on the next with nothing to explain the difference: a row had a
 * number whenever a position happened to be in the query window, and a dash
 * whenever it did not. The distinction the operator saw carried no meaning.
 *
 * The Owner rule this encodes gives the two renderings a meaning:
 *
 *   - Connection not `online`  → `dash`. A stale or offline device's last
 *     reading is history, not a live speed, and must never be presented as
 *     one. This overrides everything below.
 *   - Ignition unknown         → `unknown`. Shown as a visible label, never
 *     a bare dash, because "we do not know" is not the same statement as
 *     "there is nothing to show".
 *   - Ignition ON              → `value`, including a genuine `0`. A
 *     running vehicle standing still is a real, useful observation.
 *   - Ignition OFF, speed > `IGNITION_OFF_SPEED_MIN_KPH` → `value`. Motion
 *     without ignition is exactly the case an operator must see (towing,
 *     rolling, a miswired input), so it is never suppressed.
 *   - Ignition OFF, speed at or below that floor → `dash`. A parked
 *     vehicle's GPS noise is not information.
 *
 * A missing or non-finite speed is always `dash`: the platform reports what
 * the device sent and never invents a `0`.
 */
export type SpeedDisplay =
  | { readonly kind: "value"; readonly speedKph: number }
  | { readonly kind: "dash" }
  | { readonly kind: "unknown" };

/**
 * Ignition-off speeds at or below this (km/h) are suppressed as parking
 * noise; strictly above it the reading is surfaced.
 */
export const IGNITION_OFF_SPEED_MIN_KPH = 5;

export function resolveSpeedDisplay(input: {
  readonly speedKph: number | null | undefined;
  readonly ignitionOn: boolean | null | undefined;
  readonly connection: ConnectionState;
}): SpeedDisplay {
  // Stale / offline / never seen overrides every other consideration.
  if (input.connection !== "online") return { kind: "dash" };
  if (input.ignitionOn == null) return { kind: "unknown" };

  const speed = input.speedKph;
  if (speed == null || !Number.isFinite(speed)) return { kind: "dash" };

  if (input.ignitionOn) return { kind: "value", speedKph: speed };
  return speed > IGNITION_OFF_SPEED_MIN_KPH ? { kind: "value", speedKph: speed } : { kind: "dash" };
}
